import React from "react";
import styled from "styled-components";
import { Typography } from "ui-kit/Typography";
import CameraSVG from "assets/camera.svg";
import { DeviceSize } from "constants/deviceSize";

interface UserAccordionProps {
  avatar?: string | null;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  open?: boolean;
  onSettingsClick?: () => void;
}

export const UserAccordion: React.FC<UserAccordionProps> = props => {
  return (
    <Container style={{ padding: props.open ? "17px 24px 12px 13px" : "5px 7px" }}>
      <div style={{ display: 'flex', gap: "10px" }}>
        <Avatar style={{ width: props.open ? '52px' : '36px', height: props.open ? '52px' : '36px', backgroundColor: props.open ? '#E9E9E9' : '' }}>
          {props.avatar ? (
            <AvatarImage src={props.avatar} alt="User avatar" />
          ) : (
            <CameraSVG />
          )}
        </Avatar>
        {props.open && (
          <Info>
            <Typography variant="body3">{props.title}</Typography>
            {props.subtitle && (
              <StyledSubtitle variant="body4">{props.subtitle}</StyledSubtitle>
            )}
          </Info>
        )}
      </div>
      <SettingsButton
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          props.onSettingsClick?.();
        }}
        aria-label="Open profile settings"
      >
        <GearIcon />
      </SettingsButton>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  cursor: pointer;
  border-radius: 13px;
  margin: 22px 0 14px;
  border-rudius:13px;
  background:rgba(246, 246, 246, 1);
  padding: 17px 24px 12px 13px;
  &:hover {
    background-color: ${props => props.theme.palette.background.base};
  }
`;

const Avatar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 52px;
  width: 52px;
  border-radius: 50%;
  overflow: hidden;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledSubtitle = styled(Typography)`
  color: ${props => props.theme.palette.text.label};
`;

const SettingsButton = styled.button`
  width: 30px;
  height: 30px;
  border-radius: 999px;
  border: none;
  background: #ffffff;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.1s ease, box-shadow 0.15s ease, background 0.15s ease;

  &:hover {
    background: #f3f4f6;
    transform: translateY(-0.5px);
    box-shadow: 0 4px 10px rgba(15, 23, 42, 0.22);
  }

  @media (max-width: ${DeviceSize.TabletMedium}) {
    width: 34px;
    height: 34px;

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const GearIcon: React.FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 9.5C10.6193 9.5 9.5 10.6193 9.5 12C9.5 13.3807 10.6193 14.5 12 14.5C13.3807 14.5 14.5 13.3807 14.5 12C14.5 10.6193 13.3807 9.5 12 9.5Z"
      stroke="#111827"
      strokeWidth="1.6"
    />
    <path
      d="M19.4 12.9999C19.4331 12.6766 19.45 12.3412 19.45 11.9999C19.45 11.6587 19.4331 11.3232 19.4 10.9999L21.25 9.4749C21.4167 9.33824 21.4667 9.1049 21.375 8.90657L19.625 5.59324C19.5333 5.40157 19.3167 5.3099 19.1167 5.3599L16.8667 5.96657C16.4 5.6249 15.9 5.33657 15.3667 5.11657L15.0333 2.7849C15 2.57657 14.8167 2.41657 14.6 2.41657H9.4C9.18333 2.41657 9 2.57657 8.96667 2.7849L8.63333 5.11657C8.1 5.33657 7.6 5.6249 7.13333 5.96657L4.88333 5.3599C4.68333 5.3099 4.46667 5.40157 4.375 5.59324L2.625 8.90657C2.53333 9.1049 2.58333 9.33824 2.75 9.4749L4.6 10.9999C4.56692 11.3232 4.55 11.6587 4.55 11.9999C4.55 12.3412 4.56692 12.6766 4.6 12.9999L2.75 14.5249C2.58333 14.6616 2.53333 14.8949 2.625 15.0932L4.375 18.4066C4.46667 18.5982 4.68333 18.6899 4.88333 18.6399L7.13333 18.0332C7.6 18.3749 8.1 18.6632 8.63333 18.8832L8.96667 21.2149C9 21.4232 9.18333 21.5832 9.4 21.5832H14.6C14.8167 21.5832 15 21.4232 15.0333 21.2149L15.3667 18.8832C15.9 18.6632 16.4 18.3749 16.8667 18.0332L19.1167 18.6399C19.3167 18.6899 19.5333 18.5982 19.625 18.4066L21.375 15.0932C21.4667 14.8949 21.4167 14.6616 21.25 14.5249L19.4 12.9999Z"
      stroke="#111827"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

